import { formatDateTimeLocalToBack } from '@/helpers/date-time-local'
import { useLanguageStore } from '@/stores/LanguageStore'
import dayjs from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/es'
import 'dayjs/locale/pt-br'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'

export { formatDateTimeLocalToBack }

dayjs.extend(utc)
dayjs.extend(localizedFormat)
dayjs.extend(customParseFormat)

const DEFAULT_DATE = '0001-01-01T00:00:00.000Z'
const DAYJS_LOCALES = {
  pt: 'pt-br',
  en: 'en',
  es: 'es',
} as const

const DATE_TIME_FORMATS = {
  pt: 'DD [de] MMMM [de] YYYY [às] HH:mm:ss',
  en: 'MMMM DD, YYYY [at] HH:mm:ss',
  es: 'DD [de] MMMM [de] YYYY [a las] HH:mm:ss',
} as const

const MONTH_YEAR_FORMATS = {
  pt: 'MMMM [de] YYYY',
  en: 'MMMM YYYY',
  es: 'MMMM [de] YYYY',
} as const

const getCurrentLocale = () => {
  const languageStore = useLanguageStore()
  const locale = String(languageStore.locale || languageStore.lang || 'pt')

  return (locale in DAYJS_LOCALES ? locale : 'pt') as keyof typeof DAYJS_LOCALES
}

const getCurrentDayjsLocale = () => DAYJS_LOCALES[getCurrentLocale()]

const getNoInformationLabel = () => useLanguageStore().t('general.noInformation')

export const createRelativeDateRange = (days: number, end = new Date()): [Date, Date] => [
  dayjs(end).subtract(days, 'day').toDate(),
  end,
]

export const serializeLocalDayRangeToIso = (start: Date, end: Date): [string, string] => [
  dayjs(start).startOf('day').toISOString(),
  dayjs(end).endOf('day').toISOString(),
]

export const serializeLocalDayRangeToBackend = (
  start?: Date | null,
  end?: Date | null,
): [string | null, string | null] => [
  start ? formatDateTimeLocalToBack(dayjs(start).startOf('day').toDate(), true) : null,
  end ? formatDateTimeLocalToBack(dayjs(end).endOf('day').toDate(), true) : null,
]

export type VeePeriodValidationError = 'required' | 'partial' | 'invalid' | 'reversed' | 'future'

export interface VeePeriodValidationResult {
  valid: boolean
  error?: VeePeriodValidationError
  start?: Date
  end?: Date
}

export const validateMandatoryDayPeriod = (
  period: (Date | null | undefined)[] | null | undefined,
): VeePeriodValidationResult => {
  if (!period || period.length === 0) return { valid: false, error: 'required' }

  const [start, end] = period
  if (!start || !end) return { valid: false, error: 'partial' }
  if (!isValidDate(start) || !isValidDate(end)) return { valid: false, error: 'invalid' }

  const startDay = dayjs(start).startOf('day')
  const endDay = dayjs(end).startOf('day')
  if (endDay.isBefore(startDay)) return { valid: false, error: 'reversed' }

  const today = dayjs().startOf('day')
  if (startDay.isAfter(today) || endDay.isAfter(today)) return { valid: false, error: 'future' }

  return { valid: true, start, end }
}

export const isTimestampWithinInclusivePeriod = (
  timestamp: string | Date,
  periodStart: string | Date,
  periodEnd?: string | Date | null,
) => {
  const timestampValue = dayjs(timestamp).valueOf()
  const startValue = dayjs(periodStart).valueOf()
  const endValue = periodEnd ? dayjs(periodEnd).valueOf() : startValue

  return timestampValue >= startValue && timestampValue <= endValue
}

export const rangeOfDates = (start: Date, end: Date): string[] => {
  const diff = dayjs(end).diff(start, 'd') + 1
  const dates = []
  for (let day = 0; day < diff; day++) {
    const date = dayjs(start).add(day, 'day').toDate()
    dates.push(formatDateSortToShowWithTime(date) || '')
  }
  return dates
}

export const formatDateToSend = (date: Date, isUtc?: boolean) => {
  return dayjs(date)
    .utc(isUtc || false)
    .format('DD/MM/YYYY HH:mm:ss')
}

export const formatDateLongToShowWithTime = (date: any, isUtc?: boolean) => {
  if (!date || date === undefined || !isValidDate(date)) {
    return getNoInformationLabel()
  }

  const locale = getCurrentLocale()
  const formatted = dayjs(date)
    .utc(isUtc || false)
    .locale(getCurrentDayjsLocale())
    .format(DATE_TIME_FORMATS[locale])

  return formatted
}

export const formatDateLongToShowWithTimeUtc = (date?: any) => {
  if (!date || date === undefined || !isValidDate(date)) {
    return getNoInformationLabel()
  }

  const locale = getCurrentLocale()
  const formatted = dayjs(date)
    .utc(true)
    .locale(getCurrentDayjsLocale())
    .format(DATE_TIME_FORMATS[locale])

  return formatted
}

export const formatDateSortToShowWithTimeUtc = (date: any) => {
  if (!date || date === undefined || !isValidDate(date)) return getNoInformationLabel()

  return dayjs(date).utc(true).format('DD/MM/YYYY HH:mm:ss')
}

export const formatDateSortToShowWithTime = (date: any) => {
  if (!date || !isValidDate(date)) return getNoInformationLabel()

  return dayjs(date).utc(false).format('DD/MM/YYYY HH:mm:ss')
}

export const formatDateSortToShowWithTimeLiteral = (date: any) => {
  if (date == null || date === '') return null

  const parsed = dayjs(date).utc(false)
  if (!parsed.isValid()) return null

  return parsed.format('DD/MM/YYYY HH:mm:ss')
}

export const getDate = (date: any) => {
  return dayjs(date)
}

export const isValidDate = (date: any) => {
  if (!date) {
    return false
  }

  const dateStr = date.toString ? date.toString() : String(date)
  if (dateStr === DEFAULT_DATE) {
    return false
  }

  const parsed = dayjs(date)
  if (!parsed.isValid()) {
    return false
  }

  return true
}

export const formatDateToShow = (date: any) => {
  if (!date || !isValidDate(date)) return null
  return dayjs(date).format('DD/MM/YY').toString()
}

export const formatDateToShowUtc = (date: any) => {
  if (!date || !isValidDate(date)) return null
  return dayjs.utc(date).format('DD/MM/YYYY').toString()
}

export const formatDayMonthToShowUtc = (date: any) => {
  if (!date || !isValidDate(date)) return null
  return dayjs.utc(date).format('DD/MM').toString()
}

export const formatDateShortToShowUtc = (date: any) => {
  if (!date || !isValidDate(date)) return null
  return dayjs.utc(date).format('DD/MM/YY').toString()
}

export const formatDateToBack = (date: any) => {
  if (!date || !isValidDate(date)) return null
  return dayjs(date).format('YYYY-MM-DD').toString()
}

export const formatDateToBackUtc = (date: any) => {
  if (!date || !isValidDate(date)) return null
  return dayjs.utc(date).format('YYYY-MM-DD').toString()
}

export const formatDateToBackUtcWithTime = (date: any) => {
  if (!date || !isValidDate(date)) return null
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss').toString()
}

export const formateDateMonthYear = (date: any, isUtc?: boolean) => {
  if (!date || date === undefined || !isValidDate(date)) return getNoInformationLabel()

  const locale = getCurrentLocale()
  return dayjs(date)
    .utc(isUtc || false)
    .locale(getCurrentDayjsLocale())
    .format(MONTH_YEAR_FORMATS[locale])
}

export const setToEndOfDay = (date: any) => {
  if (!date || date === undefined || !isValidDate(date)) return undefined
  return new Date(date.setUTCHours(23, 59, 59))
}

export const setToStartOfDay = (date: any) => {
  if (!date || date === undefined || !isValidDate(date)) return undefined
  return new Date(date.setUTCHours(0, 0, 0))
}
